import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Kokochao V15 product shell.
 * Horizon 4.1.4 progressive product shell. Shopify product-info remains an isolated compatibility bridge for variant/media lifecycle.
 */
class KcH15ProductShell extends Component {
  #abortController = new AbortController();
  #buyObserver = null;
  #footerObserver = null;
  #mutationObserver = null;
  #hasPassedBuyButtons = false;
  #nearFooter = false;
  #variants = [];

  connectedCallback() {
    super.connectedCallback();

    this.stickyCart = this.querySelector('[data-kc-h15-sticky-cart]');
    this.productForm = this.querySelector(`#${CSS.escape(this.dataset.productFormId || '')}`);
    this.buyButtons = this.querySelector('[data-kc-h15-buy-buttons]');
    this.mainSubmit = this.querySelector('[id^="ProductSubmitButton-"]');
    this.idInput = this.productForm?.querySelector('input[name="id"]') || null;

    const variantsScript = this.querySelector('[data-kc-h15-variants]');
    if (variantsScript) {
      try {
        this.#variants = JSON.parse(variantsScript.textContent || '[]');
      } catch (_) {
        this.#variants = [];
      }
    }

    if (this.dataset.productId) RecentlyViewed.addProduct(String(this.dataset.productId));
    this.#bindEvents();
    this.#setupObservers();
    this.#syncVariantState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#buyObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  #bindEvents() {
    const { signal } = this.#abortController;

    this.productForm?.addEventListener('change', () => queueMicrotask(() => this.#syncVariantState()), { signal });
    document.addEventListener('variant-change', () => queueMicrotask(() => this.#syncVariantState()), { signal });
    document.addEventListener('option-value-selection-change', () => setTimeout(() => this.#syncVariantState(), 80), { signal });

    this.querySelector('[data-kc-h15-sticky-submit]')?.addEventListener('click', (event) => {
      event.preventDefault();
      if (!this.mainSubmit || this.mainSubmit.disabled) return;
      this.mainSubmit.click();
    }, { signal });

    document.addEventListener('toggle', () => requestAnimationFrame(() => this.#updateStickyVisibility()), {
      capture: true,
      signal,
    });

    window.addEventListener('resize', () => this.#updateStickyVisibility(), { passive: true, signal });
  }

  #setupObservers() {
    const observedBuyTarget = this.mainSubmit || this.buyButtons || this.productForm;
    if (observedBuyTarget) {
      this.#buyObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const rect = entry.boundingClientRect;
        this.#hasPassedBuyButtons = !entry.isIntersecting && rect.bottom < 0;
        this.#updateStickyVisibility();
      }, { threshold: 0.01 });
      this.#buyObserver.observe(observedBuyTarget);
    }

    const footer = document.querySelector('footer, .footer, [class*="footer-group"]');
    if (footer) {
      this.#footerObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.#nearFooter = entry.isIntersecting;
        this.#updateStickyVisibility();
      }, { rootMargin: '180px 0px 0px 0px' });
      this.#footerObserver.observe(footer);
    }

    if (this.productForm) {
      this.#mutationObserver = new MutationObserver(() => this.#syncVariantState());
      this.#mutationObserver.observe(this.productForm, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['value', 'disabled', 'checked'],
      });
    }
  }

  #currentVariant() {
    if (!this.idInput && this.productForm) {
      this.idInput = this.productForm.querySelector('input[name="id"]');
    }
    const id = this.idInput?.value ? String(this.idInput.value) : '';
    return this.#variants.find((variant) => String(variant.id) === id) || null;
  }

  #syncVariantState() {
    if (!this.productForm) return;
    this.idInput = this.productForm.querySelector('input[name="id"]') || this.idInput;
    this.mainSubmit = this.querySelector('[id^="ProductSubmitButton-"]') || this.mainSubmit;

    const variant = this.#currentVariant();
    const unavailable = !variant || !variant.available || Boolean(this.mainSubmit?.disabled);

    const stickyPrice = this.querySelector('[data-kc-h15-sticky-price]');
    const stickyVariant = this.querySelector('[data-kc-h15-sticky-variant]');
    const stickySubmit = this.querySelector('[data-kc-h15-sticky-submit]');

    if (variant && stickyPrice) stickyPrice.textContent = variant.price_formatted || '';
    if (stickyVariant && variant) {
      stickyVariant.textContent = variant.title || '';
      stickyVariant.hidden = !variant.title || variant.title === 'Default Title';
    }
    if (stickySubmit) {
      stickySubmit.disabled = unavailable;
      stickySubmit.textContent = unavailable ? 'Épuisé' : 'Ajouter au panier';
    }

    const saleBadge = this.querySelector('[data-kc-sale-badge]');
    const soldoutBadge = this.querySelector('[data-kc-soldout-badge]');
    const lowBadge = this.querySelector('[data-kc-low-stock-badge]');
    const urgency = this.querySelector('[data-kc-urgency-line]');
    const inventory = this.querySelector('[id^="Inventory-"]');

    const isSale = Boolean(variant?.available && variant.compare_at_price && variant.compare_at_price > variant.price);
    const lowStock = Boolean(
      variant?.available &&
      variant.inventory_management === 'shopify' &&
      variant.inventory_quantity > 0 &&
      variant.inventory_quantity <= 3
    );

    if (saleBadge) saleBadge.hidden = !isSale;
    if (soldoutBadge) soldoutBadge.hidden = !unavailable;
    if (lowBadge) lowBadge.hidden = !lowStock;

    if (variant && urgency) {
      urgency.classList.toggle('kc-urgency-line--stock', Boolean(variant.available));
      if (!variant.available) urgency.textContent = '⏳ Actuellement indisponible';
      else if (variant.inventory_management === 'shopify' && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5) {
        urgency.textContent = `🔥 Plus que ${variant.inventory_quantity} en stock`;
      } else if (variant.inventory_management === 'shopify' && variant.inventory_quantity > 5) {
        urgency.textContent = '✅ En stock — prêt pour préparation';
      } else if (variant.inventory_management === 'shopify' && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue') {
        urgency.textContent = '✅ Disponible sur commande';
      } else {
        urgency.textContent = '✅ Disponible à la commande';
      }
    }

    if (variant && inventory) {
      inventory.classList.remove('visibility-hidden');
      if (!variant.available) inventory.textContent = 'Rupture de stock';
      else if (variant.inventory_management === 'shopify' && variant.inventory_quantity > 0 && variant.inventory_quantity <= 3) {
        inventory.textContent = `Stock faible — plus que ${variant.inventory_quantity}`;
      } else if (variant.inventory_management === 'shopify' && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue') {
        inventory.textContent = 'Disponible sur commande';
      } else {
        inventory.textContent = 'Produit en stock';
      }
    }

    this.#updateStickyVisibility();
  }

  #hasBlockingOverlay() {
    return Boolean(document.querySelector(
      'theme-drawer dialog[open], dialog[open]'
    ));
  }

  #isChatActive() {
    const chat = document.querySelector('shopify-chat');
    if (!chat) return false;
    const style = getComputedStyle(chat);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  #updateStickyVisibility() {
    if (!this.stickyCart) return;
    const variant = this.#currentVariant();
    const available = Boolean(variant?.available) && !this.mainSubmit?.disabled;
    const shouldShow =
      this.#hasPassedBuyButtons &&
      !this.#nearFooter &&
      available &&
      !this.#hasBlockingOverlay() &&
      !this.#isChatActive();

    if (shouldShow) {
      this.stickyCart.hidden = false;
      requestAnimationFrame(() => this.stickyCart?.classList.add('is-visible'));
    } else {
      this.stickyCart.classList.remove('is-visible');
      window.setTimeout(() => {
        if (!this.stickyCart?.classList.contains('is-visible')) this.stickyCart.hidden = true;
      }, 230);
    }
  }
}

if (!customElements.get('kc-h15-product-shell')) {
  customElements.define('kc-h15-product-shell', KcH15ProductShell);
}
